<template>
    <div 
        class="mb-4 border rounded-lg p-125 shadow-sm"
        :class="isDisplayNode ? 'bg-white' : 'bg-gray-100'"
    >
        <div class="mb-4">
            <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
            <div class="mb-3 mt-0 d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div class="">
                    <h3 class="mb-0 font-weight-bold text-primary-darker" style="font-size: 22px;">{{ localNodeData.title }}</h3>
                    <p v-if="localNodeData.description" class="mt-2 mb-0">{{ localNodeData.description }}</p>
                </div>
                <div v-if="!isDisplayNode" class="text-right ml-auto">
                    <b-badge v-if="isWaiting && !flowIsInFinalState" variant="soft-secondary" class="font-size-100 text-uppercase">Waiting</b-badge>
                    <b-badge v-if="isSkipped" variant="soft-secondary" class="font-size-100 text-uppercase">Skipped</b-badge>
                    <b-badge v-if="isActive" variant="soft-primary" class="font-size-100 text-uppercase">Active</b-badge>
                    <b-badge v-if="isCompleted" variant="soft-success" class="font-size-100 text-uppercase">Complete</b-badge>
                    <b-badge v-if="isExpired" variant="soft-warning" class="font-size-100 text-uppercase">Expired</b-badge>
                    <div v-if="isCompleted || isActive || isExpired" class="font-size-80 mt-2" style="min-height: 2.5rem;">  <!-- to avoid the jump when localNodeData loads -->
                        <div v-if="isActive && deadlineFormatted" class="font-weight-bold">
                            <span class="text-uppercase text-muted mr-1">Due:</span>{{ deadlineFormatted }}
                            <br>
                            <span class="text-muted font-weight-normal">{{ deadlineTimeUntil }}</span>
                        </div>
                        <div v-if="isExpired && expiredAtFormatted" class="font-weight-bold">
                            <span class="text-uppercase text-muted mr-1">Expired:</span>{{ expiredAtFormatted }}
                        </div>
                        <div v-if="isCompleted"> 
                            <span class="text-muted font-weight-normal">
                                Completed {{ completedFormatted }}
                            </span>
                            <br>
                            <span v-if="localNodeData.completed_by">
                                by {{ localNodeData.completed_by.full_name }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <hr class="bg-gray-400">
            <div v-if="!isDisplayNode" class="d-flex align-items-start justify-content-between gap-3" style="">
                <div class="">  <!-- LEFT SIDE -->
                    <div v-if="showAsssigneeArea" class="spl-animate-fast spl-animate-fade-down">  <!-- d-flex gap-2 flex-wrap align-items-baseline -->
                        <div class="d-flex flex-wrap align-items-center mb-2">
                            <div class="special-text mr-2">Assignees:</div>
                            <div class="text-muted font-weight-500 text-uppercase font-size-60">{{ assignedObjectRoles.map(or => or.name).join(' + ') }}</div>
                            <!-- STILL SHOW THIS BEFORE STARTED SO WE KNOW WHO WILL BE ASSIGNED - TODO -->
                            <!-- <div 
                                v-for="objRole in assignedObjectRoles" 
                                :key="objRole.slug" 
                                class="font-size-60 text-uppercase rounded-pill bg-gray-200 px-2 py-1 line-height-100" 
                                style="color: var(--gray-600)"
                            >
                                {{ objRole.name }}
                            </div> -->
                        </div>
                        <!-- {{ assignedTuIds }} -->
                        <div class="d-inline-flex gap-2 align-items-center flex-wrap">
                            <tenant-user-badge
                                v-for="atu in assignedTus"
                                :key="atu.id"
                                :tu-data="atu"
                            ></tenant-user-badge>
                            <b-badge v-if="assignedTus.length === 0" variant="secondary" class="text-uppercase">No Assignees</b-badge>
                            <b-button
                                v-if="canChangeAssignee"
                                variant="minimal-secondary"
                                size="sm"
                                class="rounded-pill special-text font-size-70"
                                style="padding-top: 2px; padding-bottom: 2px;"
                                @click="startTransition('change_assignee')"
                            >
                                <b-icon icon="people"></b-icon>
                                Change Assignee(s)
                            </b-button>
                        </div>
                    </div>
                </div>
                <div class="d-flex gap-1 flex-wrap justify-content-end">  <!-- RIGHT SIDE -->
                    <div class="d-flex gap-1 flex-wrap justify-content-end">
                        <b-button 
                            v-for="trans in nodeTransitionsByZone['task-admin']"
                            :key="trans.trigger"
                            v-b-tooltip.hover 
                            :title="trans.description"
                            variant="default"
                            size="sm"
                            class="d-flex gap-1 align-items-center justify-content-center text-nowrap"
                            @click="startTransition(trans.trigger)"
                        >
                            <b-icon v-if="trans.icon" :icon="trans.icon" variant="secondary" class="mr-1"></b-icon>
                            {{ trans.label }}
                        </b-button>
                    </div>
                    <div class="d-flex gap-1 flex-wrap justify-content-end">
                        <b-button 
                            v-for="trans in nodeTransitionsByZone['task-actions']"
                            :key="trans.trigger"
                            v-b-tooltip.hover
                            :title="trans.description"
                            variant="default"
                            size="sm"
                            class="d-flex gap-1 align-items-center justify-content-center text-nowrap"
                            @click="startTransition(trans.trigger)"
                        >
                            <b-icon v-if="trans.icon" :icon="trans.icon" variant="secondary" class="mr-1"></b-icon>
                            {{ trans.label }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
        <!-- <div class="my-4 border rounded-lg bg-gray-100 p-3 overflow-hidden shadow-sm"> -->
        <div class="">  <!-- area for showing notifications/flags, etc. -->
            <div v-if="showRejectionEvent" class="alert alert-danger">
                <p class="mb-2">
                    <span class="font-weight-bold">Rejected</span>
                    <span class="font-italic">
                        {{ rejectionEvent.by ? ` by ${rejectionEvent.by} ` : '' }} on {{ rejectionEvent.created }}
                    </span>
                </p>
                <hr v-if="rejectionEvent.message" class="my-2">
                <p v-if="rejectionEvent.message" class="mb-0">{{ rejectionEvent.message }}</p>
            </div>
        </div>
        <div class="">
            <FlowNodeDisplay
                v-if="localNodeData.node_type === 'display'"
                key="isDisplayNode"
                ref="taskContent"
                :flowObjectClass="flowObjectClass"
                :nodeData="localNodeData"
                :nodeContext="nodeContext"
            ></FlowNodeDisplay>  <!-- display nodes will always have isWaiting true since they never start... so keep it ahead of others  -->
            <!-- <FlowNodeForms
                v-else-if="localNodeData.node_type === 'update_form'"
                key="activeUpdateForm"
                ref="taskContent"
                :flowObjectClass="flowObjectClass"
                :nodeData="localNodeData"
                :nodeContext="nodeContext"
                @updated="emitUpdated"
            ></FlowNodeForms> -->  <!-- no longer needed - have it in nodeData: :forms="localNodeData.forms" -->
            <div v-else-if="isWaiting || isSkipped" key="taskIsWaitingOrSkipped" class="d-flex flex-column align-items-center justify-content-center min-height-200">
                <!-- move this to each node type... let them figure out what to show if waiting - then maybe you don't need 'update_form' -->
                <h5 class="text-muted">
                    {{ isSkipped ? 'Skipped' : (flowIsInFinalState ? 'Not Completed' : waitingText) }}
                </h5>
                <p v-if="isSkipped && skipMessage" class="text-muted text-center">{{ skipMessage }}</p>
            </div>
            <template v-else-if="localNodeData.node_type === 'form'">
                <FlowNodeForms
                    v-if="hasNodeDetail"
                    key="activeFormTask"
                    ref="taskContent"
                    :flowObjectClass="flowObjectClass"
                    :nodeData="localNodeData"
                    :nodeContext="nodeContext"
                    @updated="emitUpdated"
                ></FlowNodeForms>  <!-- no longer needed - have it in nodeData: :forms="localNodeData.forms" -->
                <div v-else class="d-flex flex-column align-items-center justify-content-center min-height-200">                   
                    <b-spinner></b-spinner>
                </div>
            </template>
            <FlowNodeApproval
                v-else-if="localNodeData.node_type === 'approval'"
                key="activeApprovalTask"
                ref="taskContent"
                :flowObjectClass="flowObjectClass"
                :nodeData="localNodeData"
                :nodeContext="nodeContext"
                @updated="emitUpdated"
            ></FlowNodeApproval>
            <FlowNodeChoices
                v-else-if="localNodeData.node_type === 'choice'"
                key="activeChoiceTask"
                ref="taskContent"
                :flowObjectClass="flowObjectClass"
                :nodeData="localNodeData"
                :nodeContext="nodeContext"
            ></FlowNodeChoices>
        </div>
        <!-- </div> -->
        <!-- <div class="my4">
            <pre>{{ localNodeData }}</pre>
        </div> -->

        <div v-if="hasTaskBottomTransitions" class="row" :class="localNodeData.node_type === 'form' ? 'mt-3' : ''">
            <div class="col-auto text-right font-weight-600 text-uppercase font-scale-90 text-gray-600">Additional<br/>Actions</div>
            <div class="col">
                <div class="d-flex col-gap-3 row-gap-2 flex-wrap align-items-center">
                    <b-button 
                        v-for="trans in nodeTransitionsByZone['task-bottom']"
                        :key="trans.trigger"
                        v-b-tooltip.hover
                        :title="trans.description"
                        variant="default"
                        size="md"
                        class="d-flex gap-1 align-items-center justify-content-center text-nowrap"
                        @click="startTransition(trans.trigger)"
                    >
                        <b-icon v-if="trans.icon" :icon="trans.icon" :variant="trans.color_variant" class="mr-1"></b-icon>
                        {{ trans.label }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- TRANSITIONS for NODES - handle all here for all child components -->
        <b-modal
            v-model="transModalShowing"
            centered
            no-close-on-backdrop
            hide-header
            hide-footer
            :size="transModalSize"
            @hidden="handleTransModalHidden"
        >
            <h4 class="mb-4">{{ transActive.confirm_title }}</h4>
            <p 
                v-for="para in transActive.confirm_description"
                :key="para"
            >{{ para }}</p>
            <!-- these collapse if no content -->
            <h5>{{ transFormTitle }}</h5>
            <p>{{ transFormInstructions }}</p>

            
            <div
                v-if="transActive.trigger === 'change_assignee'"
                key="changeAssigneeTransForm"
                class="mt-3"
            >
                <LoadingContext :loading="changeAssigneeOptionsLoading">
                    <hr>
                    <TenantUserPicklist
                        v-model="transFormData.assigned_tu_ids"
                        :options="changeAssigneeOptions"
                        picklistTitle="Possible Assignees:"
                        selectedTitle="Selected Assignees:"
                    ></TenantUserPicklist>
                    <hr>
                    <div class="d-flex gap-3 align-items-center justify-content-center">
                        <!-- TODO - need to disable these buttons when loading, and disable submit if input is not valid -->
                        <b-button
                            variant="subtle-primary"
                            size="lg"
                            :disabled="loadingTransition"
                            @click="handleTransFormCancel"
                        >Cancel</b-button>
                        <b-button
                            variant="primary"
                            size="lg"
                            :disabled="loadingTransition || (transFormData.assigned_tu_ids && transFormData.assigned_tu_ids.length === 0)"
                            @click="handleTransManualSubmit"
                        >Save Changes</b-button>
                    </div>
                </LoadingContext>
            </div>

            <div v-else key="normalTransForm" class="mt-3">
                <FormulateForm
                    v-model="transFormData"
                    :schema="transFormSchema"
                    @submit="handleTransFormSubmit"
                    #default="{ isLoading, hasErrors }"
                >
                    <div class="mt-5 d-flex justify-content-end gap-3">
                        <FormulateInput
                            type="button"
                            label="Cancel"
                            :outer-class="['minimal']"
                            @click="handleTransFormCancel"
                            :disabled="isLoading"
                        />
                        <!-- Modal header (and therefore close button) is hidden so we must always show this -->
                        <FormulateInput
                            type="submit"
                            name="submit"
                            :label="isLoading ? 'Loading...' : transActive.label"
                            :disabled="isLoading || hasErrors"
                        />
                    </div>
                </FormulateForm>
            </div>
        </b-modal>

    </div>
</template>

<script>
import { 
    getFlowObjectApi,
} from '../../js/api-service';
import { errorHandling, currentUserInfoMixin, tenantInfoMixin } from '../../js/custom-vue-mixins';
import { FormatDateTimeLocal, HumanizeDateTimeHelper } from '../../js/filters-helpers';
import merge from 'lodash/merge';
//import invoke from 'lodash/invoke';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import FlowNodeForms from './FlowNodeForms.vue';
import FlowNodeApproval from './FlowNodeApproval.vue';
import FlowNodeChoices from './FlowNodeChoices.vue';
import FlowNodeDisplay from './FlowNodeDisplay.vue';
import TenantUserPicklist from '../tenant_users/TenantUserPicklist.vue';
import LoadingContext from '../shared/LoadingContext.vue';

// MAKE THIS A MIXIN?  THEN USE IN DIFFERENT TYPES TO DISPLAY DIFFERENTLY
// for forms, load those as soon as possible, as long as you have nodeKey, formkey?

export default {
    name: 'FlowNode',
    components: {
        FlowNodeForms,
        FlowNodeApproval,
        FlowNodeChoices,
        FlowNodeDisplay,  // just another 'task/node' type
        TenantUserPicklist,
        LoadingContext,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
        tenantInfoMixin,
    ],
    props: {
        flowObjectClass: {
            type: String,
            required: true,
        },
        flowSubtype: {
            type: String,
            default: null,
        },
        flowObjectId: {
            type: String,
            required: true,
        },
        nodeKey: {
            type: String,
            required: true,
        },
        nodeData: {  // pass in what you have from parent... we use that to show what we can, then update a local copy 
            type: Object,
            required: true,
        },
        flowContext: {  // a bunch of things passed down from parent
            type: Object,
            required: true,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            hasNodeDetail: false,  // set to true AFTER we fetched detail from API
            localNodeData: {},  // from api 
            // TRANSITIONS FOR NODES
            loadingTransition: false,
            transModalShowing: false,  // v-model for modal
            transModalSize: 'md',  // set it dynamically based on what its showing
            transActive: {},  // the active transition object
            transFormTitle: '',  // TITLE of the transition form, if any 
            transFormInstructions: '',  // INSTRUCTIONS of the transition form, if any 
            transFormData: {},  // DATA on the form for transition
            transFormSchema: [],  // SCHEMA of the transition form, if any 
            changeAssigneeOptions: [],   // list of TU objects for change_assignee transition -- must reset this when you reset other transition things
            changeAssigneeOptionsLoading: false,  
            //nodeTypeLookup: {},  // add later... so you can do <component :is=""> for different node types
        }
    },
    computed: {
        flowIsInFinalState() {
            return this.flowContext.isInFinalState;
        },
        nodeTransitions() {  // TASK/NODE transitions, not flow object
            return this.localNodeData?.transitions ?? [];
        },
        nodeTransitionsByZone() {
            // keys are zone names, values are lists of transition objects
            // we want to sort by position -- but we'll do it once at the beginning, then all arrays we split into will also be sorted
            const byZone = {};
            sortBy(this.nodeTransitions, ['position']).forEach(t => {
                const tzones = t.zones ?? [];
                tzones.forEach(z => {
                    byZone[z] = byZone[z] || [];
                    byZone[z].push(t);
                });
            });
            return byZone;
        },
        hasTaskBottomTransitions() {
            return this.nodeTransitionsByZone['task-bottom'] && this.nodeTransitionsByZone['task-bottom'].length > 0;
        },
        transitionPending() {
            // is there an active transition?  used to block other ones at the same time
            return this.transActive?.trigger ? true : false;
        },
        nodeTriggers() {
            return this.nodeTransitions.map(nt => nt.trigger);
        },
        canSendReminder() {
            return this.nodeTriggers.includes('send_reminder');
        },
        canSendAssigneeEmail() {
            return this.nodeTriggers.includes('send_assignee_email');
        },
        canChangeAssignee() {
            return this.nodeTriggers.includes('change_assignee');
        },
        canExpire() {
            return this.nodeTriggers.includes('manual_expire');
        },
        canUndoExpire() {
            return this.nodeTriggers.includes('undo_expire');
        },
        // transitions for each zone... split out - then just iterate over what's in that zone... easier than doing can... for each one
        toSummary() {
            // return method from parent to return to summary tab 
            return this.flowContext?.toSummary ? this.flowContext.toSummary : () => {};  // otherwise just use a dummy method to avoid errors
        },
        nodeContext() {
            // context object for NODE-LEVEL stuff to pass to children 
            // TODO? move all other computed properties below in here?  then you have consistency between this and the mixin
            return {
                flowObjectId: this.flowObjectId,
                nodeKey: this.nodeKey,
                loadingNode: this.loading,
                hasNodeDetail: this.hasNodeDetail,  // gets set true after we load node detail 
                nodeTransitions: this.nodeTransitions,
                nodeTriggers: this.nodeTriggers,
                nodeTransitionsByZone: this.nodeTransitionsByZone,
                startTransition: this.startTransition,  // call it with triggerName to start process, show confirm modal
                // also pass in callTransition?  so you can call transitions directly, without modal?
                loadingTransition: this.loadingTransition,  // loading state 
                flowContext: this.flowContext,
                // method for returning to summary tab in parent view, if any?  just get directly from flowContext
            };
        },
        // COMMON THINGS (child components have a mixin for these things)
        // TASK STATE 
        wasStarted() {
            return this.localNodeData.was_started;
        },
        isWaiting() {
            // might have to adjust this as you make other node types...
            // for now, if NOT started yet, and NOT editable, then it's waiting
            // because we have some node types that never get started until just in time, but are still editable (update_form)
            return !this.wasStarted && !this.isEditable && !this.isSkipped;
        },
        waitingText() {
            // text to show if we are waiting - uses 'waiting_text' in node spec, or a default set here
            return this.localNodeData.waiting_text || 'Waiting on other tasks';
        },
        isActive() {
            return this.localNodeData.is_active;
        },
        isCompleted() {
            return this.localNodeData.is_completed;
        },
        isSkipped() {
            return this.localNodeData.is_skipped;
        },
        isExpired() {
            return this.localNodeData.is_expired;
        },
        isEditable() {
            return this.localNodeData.is_editable;
        },
        skipEvent() { // if isSkipped, we'll have skip_event in localNodeData -- get created/message from that
            return this.localNodeData?.skip_event ?? null;
        },
        skipMessage() {  // from event_kwargs.message in skip_event - if isSkipped
            return this.skipEvent?.event_kwargs?.message ?? '';
        },
        showRejectionEvent() {
            return !!this.localNodeData.rejection_event && this.isActive;  // no need to show these if not active 
        },
        rejectionEvent() {  // normalize the rejection event, pull out what you need
            const rejEvent = this.localNodeData.rejection_event;
            if (!rejEvent) {
                return {};
            } 
            return {
                created: FormatDateTimeLocal(rejEvent.created),
                message: rejEvent.event_kwargs?.message || '',
                by: rejEvent.tenant_user?.full_name || '',
            }
        },
        title() {
            return this.localNodeData.title;
        },
        // DATETIME FIELDS - with formatting 
        started() {  // datetime
            return this.localNodeData.started;
        },
        startedFormatted() {
            return FormatDateTimeLocal(this.started);
        },
        completed() {  // datetime
            return this.localNodeData.completed;
        },
        completedFormatted() {
            return FormatDateTimeLocal(this.completed);
        },
        deadline() {
            return this.localNodeData.deadline;
        },
        deadlineFormatted() {
            return FormatDateTimeLocal(this.deadline);
        },
        deadlineTimeUntil() {
            return HumanizeDateTimeHelper(this.deadline);
        },
        expiredAt() {  // datetime
            return this.localNodeData.expired_at;
        },
        expiredAtFormatted() {
            return FormatDateTimeLocal(this.expiredAt);
        },
        // ASSIGNEE / ROLE RELATED
        showAsssigneeArea() {
            // show it if you have assignees OR if the change_assignee transition is available (means its a user task)
            return (!!this.assignedTus && !!this.assignedTus.length) || this.canChangeAssignee;
        },
        assignedTus() {
            return this.localNodeData.assigned_tus;  // array of objects
        },
        assignedTuIds() {
            return this.localNodeData.assigned_tus.map(atu => atu.id);  // array of IDs for now
        },
        isAssignee() {
            return this.assignedTuIds.includes(this.$currentUserInfo.tenant_user_id);
        },
        assignedObjectRoles() {
            // return array of objects for the ObjectRoles where the assigned_tus come from -- ONLY IF assigned_tus are based on object roles
            const assignedObjectRoleSlugs = this.localNodeData?.assigned_object_role_slugs || [];
            // TODO - try to get the flowObject object_roles from central flowObject.js store instead, then you don't need generic retrieve endpoint
            const allObjectRoles = this.flowContext?.flowObject.object_roles || [];
            return allObjectRoles.filter(or => assignedObjectRoleSlugs.includes(or.slug));
        },
        // NODE TYPE
        isDisplayNode() {
            return this.localNodeData?.node_type === 'display';
        },
        // API
        flowObjectApi() {  // move to data and set in created()?
            return getFlowObjectApi(this.flowObjectClass, this.flowSubtype);
        },
    },
    created() {
        this.localNodeData = cloneDeep(this.nodeData);
    },
    mounted() {
        this.getNodeData();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        refresh() {
            // reload self... when things get updated, etc. 
            this.getNodeData();
        },

        async getNodeData() {
            this.loading = true;
            let resp;
            try {
                resp = await this.flowObjectApi.get_node_detail(this.flowObjectId, this.nodeKey);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                // TODO - March 2025 - why merge existing localNodeData with resp.data????  that will prevent keys from being unset/removed if needed... NEED TO TEST THIS
                this.localNodeData = merge({}, this.localNodeData, resp.data);  // gotta set a fresh object... just adding keys to current one is NOT reactive
                this.hasNodeDetail = true;
            }
            this.loading = false;
        },
        async setChangeAssigneeOptions() {
            // set it to loading, fetch options, then set them in data -- do it all here 
            this.changeAssigneeOptionsLoading = true;
            let resp;
            let options = [];
            try {
                resp = await this.flowObjectApi.get_change_assignee_options(this.flowObjectId, this.nodeKey);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                options = resp.data;  // list of TUs
            }
            //return options;
            this.changeAssigneeOptions = [...options];
            this.changeAssigneeOptionsLoading = false;
        },

        // TRANSITION METHODS
        canTrigger(triggerName) {
            return this.nodeTriggers.includes(triggerName);
        },
        async startTransition(triggerName, transData, callDirectly) {  // need async or not?
            // START the process of a TRANSITION -- SHOW THE CONFIRM MODAL (or skip that if confirmation is being skipped)
            // transData will get MERGED into the transFormData object -- so you can pass in data to populate the form? or just pass through to callTransition
            // callDirectly -- if true, we will NOT show modal, just call the transition directly

            if (this.transitionPending) {
                this.$toast('Another action is currently pending. Try later.');
                return;
            }
            // clone any data we passed in, so we can safely merge in with local data here 
            const passedTransData = transData ? cloneDeep(transData) : {};
            // nothing else active?  reset things for good measure? before we start adding stuff again...
            this.resetTrans();
            const transObj = this.nodeTransitions.find(trans => (trans.trigger === triggerName));
            if (!transObj) {
                console.error(`Tried calling transition trigger ${triggerName} but it is not a valid option`);
                this.$toast('This action is no longer an option');
                return;
            }

            let transModalSize;  // declare here, then set it for any special transitions... if not set, then set a default at the end

            // HANDLE ANY SPECIAL TRANSITIONS (where we define form schema here vs. backend, etc.)
            // you could also have an endpoint for 'get_node_trans' that gets the node transition objects dynamically from backend... that would likely eliminate need for these
            const specialTransObj = {};
            if (triggerName === 'change_assignee') {
                // for change_assignee, we need to get list of possible assignees dynamically, so we build the schema here 
                this.setChangeAssigneeOptions();  // fire and forget... will have loader in modal if still loading when we show modal
                const changeAssigneeForm = {
                    form: {
                        title: '',  // not needed... we have stuff in confirm_title from backend
                        instructions: '',  // not needed... we have stuff in confirm_description from backend
                        items: [],  // schema is not needed -- we have a hard-coded form for this transition (but we still need to populate data)
                    },
                    data: {
                        assigned_tu_ids: [...this.assignedTuIds],
                    },
                };
                specialTransObj.form = changeAssigneeForm;  // it's nested inside another 'form' attribute
                transModalSize = 'md';
            }

            this.transActive = Object.freeze(Object.assign({}, transObj, specialTransObj));  // freeze it -- this we can always pull from here -- until we replace it on reset

            // CHANGED STRUCTURE, now using same form structure as other form nodes
            // COMBINE THESE INTO A SINGLE OBJECT?  MAKE THEM COMPUTEDS?
            // make sure all these things don't error even if we are using callDirectly!
            this.transFormTitle = this.transActive.form?.form?.title || '';
            this.transFormInstructions = this.transActive.form?.form?.instructions || '';
            // for the transition payload/data, we merge things we get from backend with any other local things passed in when calling this
            const backendTransFormData = this.transActive.form?.data ? cloneDeep(this.transActive.form.data) : {};
            this.transFormData = Object.assign({}, backendTransFormData, passedTransData);  // data passed in should take priority?
            this.transFormSchema = this.transActive.form?.form?.items ? cloneDeep(this.transActive.form?.form?.items) : [];  // form.form has {title, instructions, items}
            this.transModalSize = transModalSize ?? (this.transFormSchema && this.transFormSchema.length) ? 'md' : 'sm';  // if didn't already set transModalSize above

            // check if you have anything to show in confirm modal - confirm_title, confirm_description (likely an array, but maybe empty)
            const hasConfirmTitle = !!this.transActive.confirm_title;
            const hasConfirmDescription = Array.isArray(this.transActive.confirm_description) ? !!this.transActive.confirm_description.length : !!this.transActive.confirm_description;
            const hasTransFormSchema = !!this.transFormSchema.length;
            const willShowModal = !callDirectly && [hasConfirmTitle, hasConfirmDescription, hasTransFormSchema].some(h => !!h);

            /* // on the collect_payload.items - items may have 'default' key - need to pop those off and set in the formData object as defaults
            // also need to 'promote' any 'attrs' dict up to top-level - was nested in backend schema
            this.transFormData = {};
            this.transFormSchema = [];
            const rawSchema = this.transActive.collect_payload?.items ?? [];
            rawSchema.forEach(item => {
                if ('default' in item) {
                    this.transFormData[item.name] = item.default;
                    delete item.default;
                }
                if ('attrs' in item) {
                    Object.assign(item, item.attrs);
                    delete item.attrs;
                }
                this.transFormSchema.push(item);
            }); */
            // submit & cancel buttons - put in template

            if (willShowModal) {
                this.$nextTick(() => {
                    this.transModalShowing = true;
                });
            } else {
                // nothing to show in confirm modal?  just callTransition directly
                // pass along any hard-coded data you had in the transition... likely none
                await this.callTransition(this.transActive, this.transFormData);
            }
        },
        async handleTransManualSubmit() {
            // use this when you're calling a node transition that does not use FormulateForm (otherwise use handleTransFormSubmit directly)
            const submitData = cloneDeep(this.transFormData);
            await this.handleTransFormSubmit(submitData);
        },
        async handleTransFormSubmit(submitData) {
            // handle the submission of a TRANSITION form / confirmation
            // this is NOT the same as the submission of a 'form' node/task type
            // this is what gets called when the confirmaton/collect_data modal pops up and user clicks "confirm" -- we go here
            // make sure this is async since we call it from @submit event of formulate form, which expects an async function to show loading, make sure uploads complete, etc.
            console.log('submitData from transition', submitData);
            await this.callTransition(this.transActive, submitData);
        },
        async callTransition(transObj, collectData) {
            console.log('HELLO FROM callTransition');
            // pass in 2 args -- 1) full transition object and 2) any collectData we gathered, if required

            // ADDING CALLBACK HERE AS A HOOK POINT FOR THE TASK CONTENT TO DO THINGS BEFORE ACTUALLY SUBMITTING IT
            // IN CASE FORMS NEED TO SUBMIT BEFORE NODE IS COMPLETED, ETC.
            // HOW IT WORKS:  we added a .beforeCallTransition() method to the TaskContentMixin
            //               that should always return a promise that resolves to boolean - True if OK to proceed, false if not
            //               then you can override that method on components that use the mixin -- but make sure signature/return is same

            // TOOD - need to figure out how to get ref for the active task content component 
            // then call .beforeCallTransition()
            const taskContentComp = this.$refs['taskContent'];
            let canProceed = true;  // default
            if (taskContentComp) {
                // gotta await this!!! sometimes the form save happens in here - actually NO, not async
                //console.log('CALLING beforeCallTransition NEXT ON THIS taskContentComp:', taskContentComp);
                canProceed = await taskContentComp.beforeCallTransition({transition: transObj, data: collectData});
                //console.log('DONE WITH beforeCallTransition');
            }
            if (!canProceed) {
                console.log('in callTransition - taskContentComp rejected the transition - resetting things');
                this.resetTrans();  // this will close the transition confirm modal and reset all things
            }
            // OK... carry on

            // do we need to prep collectData for sending??? set defaults?  change types???
            // need to put things into one object as backend is expecting
            const finalCollectData = collectData ? { ...collectData } : {};
            const triggerData = {
                trigger: transObj.trigger,
                node_key: this.nodeKey,
                ...finalCollectData,
            };
            this.loadingTransition = true;
            let respData = {};  // object we'll pass to handleStateChange() - just a default
            try {
                const resp = await this.flowObjectApi.call_node_transition(this.flowObjectId, triggerData);
                // successful?  emit something to parent to refresh?  just set respData, then handle at the end
                respData = resp?.data || {};
                // {  // standard response from DWF transitions as of Oct 4 2022
                //     success (bool), 
                //     message (string), 
                //     object (serialized object being transitioned, maybe with sparse fields),
                //     redirect_url, (optional, added Oct 4 2022),
                // }
                // don't change what you pass up here unless you do it for ALL things that use this component, because they may have other things that call methods with same signature/params
            } catch (err) {
                console.log('got an error - below');  // so we can see things in RUM tracking
                console.log(err);
                console.log(typeof err);
                console.log(err.toJSON());
                // err.response is where all the good stuff is:  { config, data, headers, request, status, statusText }
                if (err.response?.status === 403) {  // if we got 403, either we have someone trying to do bad things, or the state just got updated and that transition is no longer available (this has actually happened)
                    this.$toast('That action is no longer available.  Try refreshing the page, then try again.');
                } else {
                    this.apiErrorHandler(err, 'Error while processing this.  Please contact Supplios support.');
                }
            }
            this.loadingTransition = false;  // although we might want to refresh right away?
            this.transModalShowing = false;
            // TODO - figure out what to do next... show success message?  redirect?
            // we will emit success/message to parent, it will display that in place of this task, then go to summary tab
            // NOTE - some transitions should go back to summary, some should not
            if (respData.success) {
                const message = respData.message || 'Success';
                this.emitTransitionCompleted({success: true, message: message})
            }

            // BELOW WORKS BUT USING emitCompleted INSTEAD
            // if (respData.success && respData.message) {  // check what you're actually getting back here 
            //     this.$toast.success(respData.message);
            // }
            // this.emitUpdated();  // TODO - what to do here?
            // this.toSummary();

            // if not redirecting / not successful?  error message should have already appeared....
            // this.refresh();

        },
        emitUpdated() {
            // just tell parent we updated... figure out later if/what you should pass up
            this.$emit('updated');
        },
        emitTransitionCompleted(transCompletedParams) {
            // emit success message to parent, it will show that in place of this component, then go to summary tab
            // you could also pass some key for which tab to go to instead?
            // REMEMBER - this is called on ANY transition completed, not just task completed... maybe separate them out
            const { success, message, nodeKey } = transCompletedParams;
            // if you let tabKey be undefined/null, then parent will handle going to summary
            // we'll keep same payload to parent...
            const transCompletedPayload = { success, message, nodeKey };
            this.$emit('transition-completed', transCompletedPayload);
        },
        handleTransModalHidden() {
            // reset things
            this.resetTrans();
        },
        resetTrans() {
            // reset things
            this.transModalShowing = false; // should already be false... that's how we closed, but putting here for consistency, can call this on mounted()
            this.transActive = {};  
            this.transFormTitle = '';
            this.transFormInstructions = '';
            this.transFormData = {};
            this.transFormSchema = [];
            this.changeAssigneeOptions = [];  
            this.changeAssigneeOptionsLoading = false;
            this.transModalSize = 'md';
        },
        handleTransFormCancel() {
            // handle cancel button 
            this.transModalShowing = false;  
            // other stuff gets reset on @hidden
        },

    }
}
</script>

<style>

</style>